













































































import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'
import { ADD_TOAST_MESSAGE } from 'vuex-toast'
import store from '@/store'
import {
  SCRIPTS__FETCH,
  SCRIPTS__FETCH_CARDS,
  SCRIPTS__FETCH_ONE,
  SCRIPTS__STEP_CARD,
  SCRIPTS__CREATE_OPTION,
  SCRIPTS__UPDATE_OPTION,
  SCRIPTS__REMOVE_OPTION,
} from '@/store/actions'
import BaseLayout from '@/layout/BaseLayout.vue'
import CommonBack from '@/components/Common/CommonBack.vue'
import CustomSelect from '@/components/CustomSelect/CustomSelect.vue'
import SwitchButtons from '@/components/SwitchButtons/SwitchButtons.vue'
import { RouteNames } from '@/router'
import { CardOptionType, CardOptionTypeCreate, MAX_OPTIONS_COUNT } from '@/constants/scripts'
import { AppColor } from '@/@types'

import type { RawLocation } from 'vue-router'
import type { AxiosPromise, AxiosResponse } from 'axios'
import type { GetScriptCardPayload, ScriptOptionRemovePayload } from '@/store/modules/scripts/actions'
import type {
  CardInfo,
  CardOption,
  CreateOptionPayload,
  CreateOptionPayloadData,
  SchemeCardInfo,
  ScriptUnit,
} from '@/models/scripts'
import type { Scheme } from '@/models/scheme'

interface FormData {
  type: CardOptionTypeCreate,
  name: string,
  card: SchemeCardInfo | null | undefined,
  script: ScriptUnit | null | undefined,
  newCardName: string,
  isTarget: boolean,
  color: AppColor,
}

interface Data extends FormData {
  isReady: boolean
  isSubmitting: boolean
  colorList: AppColor[]
}

interface Methods {
  [SCRIPTS__STEP_CARD]: (payload: GetScriptCardPayload) => AxiosPromise<CardInfo>
  [SCRIPTS__CREATE_OPTION]: (payload: CreateOptionPayload) => AxiosPromise<CardOption>
  [SCRIPTS__UPDATE_OPTION]: (payload: CreateOptionPayload) => AxiosPromise<boolean>
  [SCRIPTS__REMOVE_OPTION]: (payload: ScriptOptionRemovePayload) => AxiosPromise<boolean>
  fetchData: () => Promise<void>
  preparePayload: () => CreateOptionPayload
  handleCreate: () => Promise<CardOption | void>
  handleCreateAndEditScript: () => Promise<void>
  handleCreateAndEditScheme: () => Promise<void>
  handleUpdate: () => Promise<void>
  handleRemove: () => void
  extractTypeLabel: (type: CardOptionTypeCreate | undefined) => string
}

interface Computed {
  getScriptScheme: Scheme
  getScriptsList: ScriptUnit[]
  typeIsCard: boolean,
  typeIsNewCard: boolean,
  typeIsScript: boolean,
  typeIsFinal: boolean,
  isCanSubmit: boolean,
  isLastOption: boolean,
  canCreateOption: boolean,
  availableTypes: CardOptionTypeCreate[],
  availableCards: SchemeCardInfo[],
  availableScripts: ScriptUnit[],
  currentCard?: SchemeCardInfo
  currentCardOption?: CardOption
  currentCardOptionsCount: number
  schemeEditLink: RawLocation
}

const COLOR_LIST: AppColor[] = [
  AppColor.Purple,
  AppColor.Green,
  AppColor.Red,
  AppColor.Orange,
  AppColor.Blue,
]

const INITIAL_FORM: FormData = {
  type: CardOptionTypeCreate.NewCard,
  name: '',
  card: null,
  script: null,
  newCardName: '',
  isTarget: false,
  color: COLOR_LIST[0],
}

export default Vue.extend<Data, Methods, Computed, {}>({
  components: {
    BaseLayout,
    CommonBack,
    CustomSelect,
    SwitchButtons,
  },
  data() {
    return {
      isReady: false,
      isSubmitting: false,
      ...INITIAL_FORM,
      colorList: COLOR_LIST,
    }
  },
  computed: {
    ...mapGetters(['getScriptScheme', 'getScriptsList']),
    typeIsCard(): boolean {
      return this.type === CardOptionTypeCreate.Card
    },
    typeIsNewCard(): boolean {
      return this.type === CardOptionTypeCreate.NewCard
    },
    typeIsScript(): boolean {
      return this.type === CardOptionTypeCreate.Script
    },
    typeIsFinal(): boolean {
      return this.type === CardOptionTypeCreate.Final
    },
    isCanSubmit(): boolean {
      const isFormValid = (
        !!this.name &&
        !!this.color &&
        (!this.typeIsNewCard || !!this.newCardName) &&
        (!this.typeIsCard || !!this.card) &&
        (!this.typeIsScript || !!this.script)
      )

      const optionCondition = this.currentCardOption ? (
        this.type !== this.currentCardOption.type as Extract<CardOptionTypeCreate, CardOptionType> ||
        this.name !== this.currentCardOption.name ||
        this.color !== this.currentCardOption.color ||
        this.isTarget !== this.currentCardOption.t ||
        (this.typeIsCard && Number(this.currentCardOption.link) !== Number(this.card?.num)) ||
        (this.typeIsScript && Number(this.currentCardOption.link) !== Number(this.script?.id))
      ) : this.canCreateOption

      return (
        !this.isSubmitting &&
        isFormValid &&
        optionCondition
      )
    },
    isLastOption(): boolean {
      return this.currentCardOptionsCount >= MAX_OPTIONS_COUNT - 1
    },
    canCreateOption(): boolean {
      return !!this.currentCardOption || this.currentCardOptionsCount < MAX_OPTIONS_COUNT
    },
    availableTypes(): CardOptionTypeCreate[] {
      return [
        CardOptionTypeCreate.Final,
        CardOptionTypeCreate.Card,
        CardOptionTypeCreate.NewCard,
        CardOptionTypeCreate.Script,
      ]
        // TODO: minor v-close-popover bug
        // .filter(typeOption => typeOption !== this.type)
    },
    availableCards(): SchemeCardInfo[] {
      const { cardId } = this.$route.params
      return this.getScriptScheme.cards
        .filter((card: SchemeCardInfo) => card.id !== cardId)
    },
    availableScripts(): ScriptUnit[] {
      const { scriptId } = this.$route.params
      return this.getScriptsList
        .filter((script: ScriptUnit) => script.id !== scriptId)
    },
    currentCard(): SchemeCardInfo | undefined {
      const { cardId } = this.$route.params

      const cards = this.getScriptScheme.cards.filter(card => card.id === cardId)

      if (cards.length > 1) {
        throw `Wrong length, card: ${cardId}, length: ${cards.length}`
      }

      return cards[0]
    },
    currentCardOption(): CardOption | undefined {
      const { variant, optionId } = this.$route.params

      if (!this.currentCard) {
        return
      }

      const options = this.currentCard.content[variant].options.filter(
        option => option.id === optionId,
      )

      if (options.length > 1) {
        throw `Wrong length, option: ${optionId}, length: ${options.length}`
      }

      return options[0]
    },
    currentCardOptionsCount(): number {
      const { variant } = this.$route.params
      return this.currentCard?.content[variant]?.options.length || 0
    },
    schemeEditLink(): RawLocation {
      const { scriptId, instanceId } = this.$route.params

      return {
        name: RouteNames.ScriptSchemeEdit,
        params: { scriptId, instanceId },
      }
    }
  },
  methods: {
    ...mapActions([
      SCRIPTS__STEP_CARD,
      SCRIPTS__CREATE_OPTION,
      SCRIPTS__UPDATE_OPTION,
      SCRIPTS__REMOVE_OPTION,
    ]),
    async fetchData() {
      try {
        const { scriptId, instanceId } = this.$route.params
        this.isReady = false

        await Promise.all([
          store.dispatch(SCRIPTS__FETCH_ONE, { id: scriptId }),
          store.dispatch(SCRIPTS__FETCH_CARDS, { id: scriptId, instanceId }),
          store.dispatch(SCRIPTS__FETCH),
        ])

        if (this.currentCardOption) {
          this.type = this.currentCardOption.type as Extract<CardOptionTypeCreate, CardOptionType>
          this.name = this.currentCardOption.name
          this.isTarget = this.currentCardOption.t
          this.color = this.currentCardOption.color
        }

        this.isReady = true
      } catch (e) {
        store.dispatch(ADD_TOAST_MESSAGE, {
          text: e,
          type: 'danger',
        })
      }
    },
    preparePayload(): CreateOptionPayload {
      const { scriptId, cardId, instanceId, optionId, variant } = this.$route.params

      const getLink = (): number => {
        switch (this.type) {
          case CardOptionTypeCreate.Card:
            return Number(this.card?.num)
          case CardOptionTypeCreate.Script:
            return Number(this.script?.id)
          default:
            return 0
        }
      }

      const data: CreateOptionPayloadData = {
        variant,
        color: this.color,
        name: this.name,
        target: this.isTarget,
        link: getLink(),
        type: this.type,
      }

      if (data.type === CardOptionTypeCreate.NewCard) {
        data.type = CardOptionTypeCreate.Card
        data['l_name'] = this.newCardName
      }

      const payload: CreateOptionPayload = {
        id: scriptId,
        cardId,
        instanceId,
        optionId,
        data,
      }
      return payload
    },
    async handleCreate(): Promise<CardOption | void> {
      try {
        if (this.isSubmitting) {
          return
        }

        this.isSubmitting = true

        const payload = this.preparePayload()

        const response = await this[SCRIPTS__CREATE_OPTION](payload)

        Object.keys(INITIAL_FORM).forEach((key) => {
          const formDataKey = key as keyof FormData
          this.$data[formDataKey] = INITIAL_FORM[formDataKey]
        })

        this.isSubmitting = false

        this.$store.dispatch(ADD_TOAST_MESSAGE, {
          text: this.$tc('transition.actions.createSuccess'),
          type: 'success',
        })

        return response.data
      } catch (e) {
        this.isSubmitting = false
      }
    },
    async handleCreateAndEditScript(): Promise<void> {
      if (this.isSubmitting) {
        return
      }

      if (this.type !== CardOptionTypeCreate.NewCard) {
        return
      }

      const { scriptId, instanceId } = this.$route.params

      const cardOption = await this.handleCreate()

      if (!cardOption) {
        return
      }

      const cardResponse: AxiosResponse<CardInfo> = await this[SCRIPTS__STEP_CARD]({
        scriptId,
        cardNumber: Number(cardOption.link),
      })

      this.$router.push({
        name: RouteNames.ScriptEdit,
        params: {
          cardId: cardResponse.data.id,
          scriptId,
          instanceId,
        },
      })
    },
    async handleCreateAndEditScheme(): Promise<void> {
      if (this.isSubmitting) {
        return
      }

      await this.handleCreate()
      this.$router.push(this.schemeEditLink)
    },
    async handleUpdate(): Promise<void> {
      if (this.isSubmitting) {
        return
      }
      this.isSubmitting = true

      const payload = this.preparePayload()

      await this[SCRIPTS__UPDATE_OPTION](payload)

      this.$store.dispatch(ADD_TOAST_MESSAGE, {
        text: this.$tc('transition.actions.updateSuccess'),
        type: 'success',
      })

      this.isSubmitting = false

      this.$router.push(this.schemeEditLink)
    },
    handleRemove(): void {
      this.$useConfirmDialog([this.$tc('transition.actions.removeConfirm'), {
          type: 'danger',
          okText: this.$tc('common.remove'),
          cancelText: this.$tc('common.cancel'),
        }], async () => {
        if (this.isSubmitting) {
          return
        }
        this.isSubmitting = true

        const { scriptId, cardId, instanceId, optionId } = this.$route.params

        await this[SCRIPTS__REMOVE_OPTION]({
          id: scriptId,
          cardId: cardId,
          instanceId,
          optionId: optionId,
        })

        this.$store.dispatch(ADD_TOAST_MESSAGE, {
          text: this.$tc('transition.actions.removeSuccess'),
          type: 'info',
        })

        this.isSubmitting = false

        this.$router.replace(this.schemeEditLink)
      })
    },
    extractTypeLabel(type: CardOptionTypeCreate | undefined): string {
      switch (type) {
        case CardOptionTypeCreate.Final:
          return this.$tc('common.compliting')
        case CardOptionTypeCreate.NewCard:
          return this.$tc('step.newName')
        case CardOptionTypeCreate.Card:
          return this.$tc('step.currentName')
        case CardOptionTypeCreate.Script:
          return this.$tc('step.goToScript')
        default:
          return ''
      }
    }
  },
  mounted() {
    this.fetchData()
  },
  watch: {
    "$route.params"() {
      this.fetchData()
    },
    type(newValue, oldValue) {
      if (oldValue === CardOptionTypeCreate.Final) {
        this.color = this.currentCardOption?.color || this.colorList[0]
        this.name = this.currentCardOption?.name || ''
      }

      if (newValue === CardOptionTypeCreate.Final) {
        this.color = AppColor.Red
        this.name = this.$tc('common.complete')
      }

      if (newValue === CardOptionTypeCreate.Card) {
        this.card = this.currentCardOption && this.currentCardOption.type === CardOptionType.Card
          ? this.availableCards.find(card => card.num === Number(this.currentCardOption?.link))
          : this.availableCards[0]
      }

      if (newValue === CardOptionTypeCreate.Script) {
        this.script = this.currentCardOption && this.currentCardOption.type === CardOptionType.Script
          ? this.availableScripts.find(script => script.id === String(this.currentCardOption?.link))
          : this.availableScripts[0]
      }
    }
  },
  metaInfo() {
    return { title: this.$tc(`transition.${this.currentCardOption ? 'editTitle' : 'title'}`) }
  },
})
